<template>
  <div class="progress-band" :class="{ 'progress-band--no-timer': !hasTimer }">
    <div class="progress-band__nav">
      <button
        v-if="showHome"
        class="progress-nav-btn progress-nav-btn--home"
        :aria-label="homeLabel"
        @click="emit('home')"
      >
        <i class="ri-home-5-line" aria-hidden="true"></i>
      </button>
      <span v-else-if="showBack" class="progress-nav-placeholder"></span>

      <button
        v-if="showBack"
        class="progress-nav-btn"
        aria-label="Previous challenge"
        @click="emit('back')"
      >
        <i class="ri-skip-left-fill" aria-hidden="true"></i>
      </button>
      <span v-else class="progress-nav-placeholder"></span>

      <span class="progress-counter" aria-live="polite" :aria-label="`Challenge ${current + 1} of ${total}`">
        {{ current + 1 }}<span class="progress-counter__sep">/</span>{{ total }}
      </span>

      <button
        class="progress-nav-btn"
        aria-label="Next challenge"
        @click="emit('next')"
      >
        <i class="ri-skip-right-fill" aria-hidden="true"></i>
      </button>
    </div>

    <div v-if="hasTimer" class="progress-bar-track" aria-hidden="true">
      <div
        class="progress-bar-fill"
        :style="{ transform: `scaleX(${progress})`, transitionDuration: progress === 1 ? '0ms' : '1000ms' }"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  current:   { type: Number,  required: true },
  total:     { type: Number,  required: true },
  remaining: { type: Number,  default: null },
  totalSecs: { type: Number,  default: null },
  showBack:  { type: Boolean, default: false },
  showHome:  { type: Boolean, default: false },
  homeLabel: { type: String,  default: 'Home' },
});

const emit = defineEmits(['back', 'next', 'home']);

const hasTimer = computed(() => props.totalSecs != null && props.totalSecs > 0);

const progress = computed(() => {
  if (!hasTimer.value || props.remaining === null) return 1;
  return props.remaining / props.totalSecs;
});
</script>

<style scoped>
.progress-band {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;
}

.progress-band__nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.progress-counter {
  font-size: 12px;
  letter-spacing: 0.1em;
  color: var(--text-color);
  font-weight: 600;
  min-width: 3.5ch;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.progress-counter__sep {
  opacity: 0.4;
  margin: 0 1px;
}

.progress-nav-btn {
  background: none;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
  transition: color 0.12s;
  border-radius: 2px;
  -webkit-tap-highlight-color: transparent;
}

.progress-nav-btn:hover {
  color: var(--text-color-bright);
}

.progress-nav-btn:focus-visible {
  outline: 2px solid var(--text-color-bright);
  outline-offset: 2px;
}

.progress-nav-btn:focus:not(:focus-visible) {
  outline: none;
}

.progress-nav-btn:active {
  transform: scale(0.88);
  transition: transform 0.07s ease, color 0.12s;
}

.progress-nav-placeholder {
  width: 28px;
}

.progress-nav-btn--home {
  display: none;
  margin-right: 8px;
  border-right: 1px solid var(--border-color);
  padding-right: 14px;
}

@media (max-width: 430px) and (orientation: landscape) {
  .progress-nav-btn--home {
    display: flex;
  }
}

.progress-bar-track {
  width: 100%;
  height: 4px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  position: absolute;
  inset: 0;
  background-color: #5a5a5a;
  transform-origin: left center;
  transform: scaleX(1);
  transition: transform 1000ms linear;
}

@media (prefers-reduced-motion: reduce) {
  .progress-bar-fill {
    transition: none;
  }
}
</style>
